<template>
  <tr>
    <td>
      <div class="hstack gap-2">
        <AVSpinner
          v-if="isActive"
          size="sm"
          color="primary-30"
        />
        <div
          :class="trustee.connected ? 'bg-success pulse-success' : 'bg-danger'"
          class="circle-color"
          v-tooltip="
            trustee.connected
              ? t('backend.e2e.voting_round_reports.show.present')
              : t('backend.e2e.voting_round_reports.show.not_present')
          "
        >
      </div>
        {{ trustee.name }}
      </div>
    </td>
    <td>
      <div class="hstack justify-content-between me-5">
        <span :class="`badge text-bg-${mixingBadge}`">
          {{ mixingLabel }}
        </span>
        <div class="hstack gap-3">
          <a
            v-if="mixingRestartableStates.includes(mixingStatus) && trusteesUrl"
            role="button"
            class="link-dark"
            v-tooltip="t('backend.actions.retry')"
            @click="retryMixing(trustee)"
          >
            <AVIcon icon="repeat" />
          </a>
          <a
            v-if="mixingRestartableStates.includes(mixingStatus) && canCancelMixing(trustee)"
            role="button"
            class="link-danger"
            v-tooltip="t('backend.actions.skip')"
            @click="cancelMixing(trustee)"
          >
            <AVIcon icon="times" />
          </a>
        </div>
      </div>
    </td>
    <td>
      <div class="hstack justify-content-between">
        <span :class="`badge text-bg-${decryptionBadge}`">
          {{ decryptionLabel }}
        </span>
        <div class="hstack gap-3" v-if="resultOrchestrator.status === 'decrypting'">
          <a
            v-if="decryptionRestartableStates.includes(decryptionStatus) && trusteesUrl"
            role="button"
            class="link-dark"
            v-tooltip="t('backend.actions.retry')"
            @click="retryDecryption(trustee)"
          >
            <AVIcon icon="repeat" />
          </a>
          <a
            v-if="decryptionRestartableStates.includes(decryptionStatus) && canCancelDecryption(trustee)"
            role="button"
            class="link-danger"
            v-tooltip="t('backend.actions.skip')"
            @click="cancelDecryption(trustee)"
          >
            <AVIcon icon="times" />
          </a>
        </div>
      </div>
    </td>
  </tr>
</template>

<script setup lang="ts">
import apiClient from "@/entrypoints/frontend/apiClient";
import { useI18n } from "vue-i18n";
import { ref, computed } from "vue";
import {
  calculateTrusteeMixingStatus,
  calculateTrusteeDecryptionStatus,
} from "@/entrypoints/shared/trustee_status_helpers";
import type {
  PropType,
  ElectaTrustee,
  E2E,
  WorkflowState,
} from "@/types";

const { t } = useI18n();

const props = defineProps({
  trustee: {
    type: Object as PropType<ElectaTrustee>,
    required: true,
  },
  resultOrchestrator: {
    type: Object as PropType<E2E.ResultOrchestrator>,
    required: true,
  },
  threshold: {
    type: Number,
    required: true,
  },
  isActive: {
    type: Boolean,
    default: false,
  },
});

const packageSuccessStatus = ref<Array<WorkflowState>>(["initial", "starting", "mixing", "decrypting", "validating", "finished"]);

const mixingRestartableStates = ref<Array<WorkflowState>>(["initial", "starting", "mixing", "validating", "failed"]);

const decryptionRestartableStates = ref<Array<WorkflowState>>(["initial", "decrypting", "validating", "failed"]);

const mixingStatus = computed(() => calculateTrusteeMixingStatus(props.trustee, props.resultOrchestrator));

const decryptionStatus = computed(() => calculateTrusteeDecryptionStatus(props.trustee, props.resultOrchestrator));

const mixingLabel = computed(() => t(`js.result_progress.states.${mixingStatus.value}`));

const decryptionLabel = computed(() => t(`js.result_progress.states.${decryptionStatus.value}`));

const mixingBadge = computed(() => getStatusBadgeClass(mixingStatus.value));

const decryptionBadge = computed(() => getStatusBadgeClass(decryptionStatus.value));

const getStatusBadgeClass = (key: WorkflowState) => { 
  switch (key) {
    case "cancelled":
      return "info";
    case "failed":
      return "danger";
    case "finished":
    case "ready":
      return "success";
    default:
      return "secondary";      
  }
};

// As long as there is enough packages that isn't cancelled or failed we are allowed to cancel
const canCancelMixing = (trustee: ElectaTrustee) => {
  if (!trusteesUrl.value) return false;

  const mixingStatus = calculateTrusteeMixingStatus(trustee, props.resultOrchestrator);
  const remainingPackages = props.resultOrchestrator.mixingPackages?.filter((mixPackage) =>
    packageSuccessStatus.value.includes(mixPackage.status),
  ).length;

  if (packageSuccessStatus.value.includes(mixingStatus)) return remainingPackages > props.threshold;
  else return remainingPackages >= props.threshold;
};

// As long as there is enough packages that isn't cancelled or failed we are allowed to cancel
const canCancelDecryption = (trustee: ElectaTrustee) => {
  if (!trusteesUrl.value) return false;

  const decryptionStatus = calculateTrusteeDecryptionStatus(trustee, props.resultOrchestrator);
  const remainingPackages = props.resultOrchestrator.decryptionPackages?.filter((decPackage) =>
    packageSuccessStatus.value.includes(decPackage.status),
  ).length

  if (packageSuccessStatus.value.includes(decryptionStatus)) return remainingPackages > props.threshold;
  else return remainingPackages >= props.threshold;
};

const trusteesUrl = computed(() => {
  const url = new URL(window.location.href);
  const regex = /\/backend\/organisations\/(?<organisationId>\d+)\/elections\/(?<electionId>\d+)/;
  const match = url.pathname.match(regex);

  if (match) {
    const { organisationId, electionId } = match.groups;
    url.pathname = `/backend/organisations/${organisationId}/elections/${electionId}/e2e/trustees`;

    return url;
  }

  return null;
});

const cancelMixing = async (trustee: ElectaTrustee) => {
  const url = `${trusteesUrl.value}/cancel_mixing`;

  await apiClient.post(url, {
    id: trustee.id,
    mixing_package_id: props.resultOrchestrator.mixingPackages?.find(
      (mixPackage) => mixPackage.trusteeUUID === trustee.uuid,
    ).id,
  });
};

const cancelDecryption = async (trustee: ElectaTrustee) => {
  const url = `${trusteesUrl.value}/cancel_decryption`;

  await apiClient.post(url, {
    decryption_package_id: props.resultOrchestrator.decryptionPackages?.find(
      (decryptPackage) => decryptPackage.trusteeUUID === trustee.uuid,
    ).id,
    id: trustee.id,
  });
};

const retryDecryption = async (trustee: ElectaTrustee) => {
  const url = `${trusteesUrl.value}/retry_decryption`;

  await apiClient.post(url, {
    decryption_package_id: props.resultOrchestrator.decryptionPackages?.find(
      (decryptPackage) => decryptPackage.trusteeUUID === trustee.uuid,
    ).id,
    id: trustee.id,
  });
};

const retryMixing = async (trustee: ElectaTrustee) => {
  const url = `${trusteesUrl.value}/retry_mixing`;

  await apiClient.post(url, {
    id: trustee.id,
    mixing_package_id: props.resultOrchestrator.mixingPackages?.find(
      (mixPackage) => mixPackage.trusteeUUID === trustee.uuid,
    ).id,
  });
};
</script>
